import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { getThread, sendMessage } from '../../services/teacher';
import { useTeacherMessaging } from '../../contexts/TeacherMessagingContext';

export default function ThreadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const threadId = Number(id);
  const { registerNotifyHandler, refreshThreads } = useTeacherMessaging();
  const bottomRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getThread(threadId);
      setThread(res.data);
      setError(null);
      await refreshThreads();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [threadId, refreshThreads]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const un = registerNotifyHandler((data) => {
      if (Number(data.thread_id) === threadId) {
        load();
      }
    });
    return un;
  }, [threadId, registerNotifyHandler, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages]);

  const handleSend = async () => {
    const text = body.trim();
    if (!text) return;
    const optimistic = {
      id: `tmp-${Date.now()}`,
      body: text,
      direction: 'outbound',
      delivery_status: 'pending',
      created_at: new Date().toISOString(),
    };
    setThread((prev) =>
      prev
        ? {
            ...prev,
            messages: [...(prev.messages || []), optimistic],
          }
        : prev
    );
    setBody('');
    setSending(true);
    try {
      const res = await sendMessage(threadId, { body: text, channel: 'sms' });
      setThread((prev) => {
        if (!prev) return prev;
        const msgs = (prev.messages || []).filter((m) => m.id !== optimistic.id);
        return {
          ...prev,
          messages: [
            ...msgs,
            {
              id: res.data.id,
              body: res.data.body,
              direction: res.data.direction,
              delivery_status: res.data.delivery_status,
              created_at: res.data.created_at,
            },
          ],
        };
      });
      await refreshThreads();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setThread((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: (prev.messages || []).filter((m) => m.id !== optimistic.id),
        };
      });
      setBody(text);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !thread) {
    return <Alert severity="error">{error}</Alert>;
  }

  const messages = thread?.messages || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '60vh', pb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/teacher/inbox')} aria-label="Back" size="large">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {thread?.student_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Parent: {thread?.parent_name}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5 }}>
        {messages.map((m) => {
          const outbound = m.direction === 'outbound';
          return (
            <Box
              key={m.id}
              sx={{
                display: 'flex',
                justifyContent: outbound ? 'flex-end' : 'flex-start',
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  maxWidth: '85%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: outbound ? 'primary.main' : 'grey.200',
                  color: outbound ? 'primary.contrastText' : 'text.primary',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.body}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    opacity: 0.85,
                    textAlign: outbound ? 'right' : 'left',
                  }}
                >
                  {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  {outbound && m.delivery_status === 'pending' ? ' · Sending…' : ''}
                  {outbound && m.delivery_status === 'failed' ? ' · Failed' : ''}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: { xs: 56, md: 0 },
          p: 1.5,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          zIndex: 9,
        }}
      >
        <TextField
          fullWidth
          placeholder="Type a message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          multiline
          maxRows={4}
          size="small"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <IconButton color="primary" onClick={handleSend} disabled={sending || !body.trim()} aria-label="Send" sx={{ minWidth: 48, minHeight: 48 }}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
